/**
 * ContactSearch — Reusable contact-search field with:
 *   - Debounced search via GET /api/contacts/search?q={query}
 *   - Dropdown showing display_name + primary_phone/email
 *   - Fetches linked matters on selection via GET /api/contacts/{id}
 *   - Optional matter selector dropdown
 *   - Inline "Save as contact" mini-form when no results
 */
import { useState, useCallback, useEffect } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { toast } from "sonner";
import {
  Search, Loader2, User, X, UserPlus, Mail, Phone,
} from "lucide-react";
import axios from "axios";
import { API } from "../config/api";
import { isOnClientFacingPage } from "../config/clientFacingRoutes";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

import { errorMessage } from "../utils/errorMessage";
const CONTACT_TYPES = [
  "Client", "Estate agent", "Mortgage broker", "Accountant",
  "Law firm", "Other party", "Other",
];

export default function ContactSearch({
  label = "Client name",
  onSelect,
  selectedName = "",
  selectedEmail = "",
  onClear,
  onTypedName,
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(false);

  // Linked matters
  const [linkedMatters, setLinkedMatters] = useState([]);
  const [loadingMatters, setLoadingMatters] = useState(false);

  // New contact form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newContactType, setNewContactType] = useState("Client");
  const [newContactCustomType, setNewContactCustomType] = useState("");
  const [newName, setNewName] = useState("");
  const [newOrg, setNewOrg] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  const searchContacts = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    // BM-H604: this is the STAFF contacts directory. It must never be
    // queried from a client-facing page (the route is staff-sealed, and its
    // 401 once sent onboarding clients to the staff Microsoft login).
    if (isOnClientFacingPage()) { setResults([]); return; }
    setSearching(true);
    try {
      const resp = await axios.get(`${API}/contacts/search`, {
        params: { q, limit: 10 },
        withCredentials: true,
      });
      const data = resp.data;
      setResults(Array.isArray(data) ? data : data?.results || []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  }, []);

  // Brief #3 — debounce extracted to `useDebouncedValue`.
  // Behaviour preserved verbatim: 300ms window (was the existing
  // setTimeout delay below), minimum 2 chars (the `q.length < 2`
  // guard inside `searchContacts`). The hook also handles unmount
  // cleanup, so the manual `timerRef` clear-on-unmount is implicit.
  // This is the only refactor permitted in ContactSearch.jsx per
  // Brief #3 Block 4.
  const debouncedQuery = useDebouncedValue(query, 300);
  useEffect(() => {
    searchContacts(debouncedQuery);
  }, [debouncedQuery, searchContacts]);

  const handleQueryChange = useCallback((e) => {
    const q = e.target.value;
    setQuery(q);
    setSelected(false);
    setShowNewForm(false);
    setLinkedMatters([]);
    // LV-6: propagate typed text back to the parent so a form that
    // validates `client_name` as required is satisfied by free-text
    // entry (no contact pick needed).  The validator on the invoice-
    // request page used to reject typed-but-not-selected names and
    // show "Client name is required" even when the field looked
    // populated to the user.
    if (typeof onTypedName === "function") {
      onTypedName(q);
    }
    // Brief #3 — search is now driven by the debouncedQuery effect
    // above (useDebouncedValue, 300ms, behaviour-preserving). The
    // previous inline setTimeout + timerRef has been removed.
  }, [onTypedName]);

  const fetchLinkedMatters = useCallback(async (contactId) => {
    if (!contactId) return;
    setLoadingMatters(true);
    try {
      const resp = await axios.get(`${API}/contacts/${contactId}`, {
        withCredentials: true,
      });
      const contact = resp.data;
      setLinkedMatters(contact.matters || contact.linked_matters || []);
    } catch {
      setLinkedMatters([]);
    }
    setLoadingMatters(false);
  }, []);

  const selectContact = useCallback((c) => {
    const name = c.display_name || c.name || "";
    const email = c.primary_email || c.email || "";
    const contactId = c.id || c.contact_id || null;
    setSelected(true);
    setQuery("");
    setResults([]);
    setShowNewForm(false);
    onSelect({
      contact_id: contactId,
      client_name: name,
      client_email: email,
      primary_phone: c.primary_phone || "",
    });
    fetchLinkedMatters(contactId);
  }, [onSelect, fetchLinkedMatters]);

  const handleClear = useCallback(() => {
    setSelected(false);
    setQuery("");
    setResults([]);
    setLinkedMatters([]);
    setShowNewForm(false);
    if (onClear) onClear();
  }, [onClear]);

  const selectMatter = useCallback((m) => {
    const ref = m.reference || m.matter_reference || m.matter_ref || "";
    const title = m.matter_title || m.title || "";
    onSelect({
      matter_reference: ref,
      matter_title: title,
    });
  }, [onSelect]);

  const saveNewContact = useCallback(async () => {
    if (!newName.trim()) { toast.error("Contact name is required"); return; }
    setSavingContact(true);
    try {
      const contactType = newContactType === "Other"
        ? (newContactCustomType.trim() || "Other")
        : newContactType;
      const resp = await axios.post(`${API}/contacts`, {
        type: contactType,
        display_name: newName.trim(),
        organisation: newOrg.trim() || undefined,
        primary_phone: newPhone.trim() || undefined,
        primary_email: newEmail.trim() || undefined,
      }, { withCredentials: true });

      const created = resp.data;
      const createdId = created.id || created.contact_id;

      setSelected(true);
      setShowNewForm(false);
      onSelect({
        contact_id: createdId,
        client_name: newName.trim(),
        client_email: newEmail.trim(),
        primary_phone: newPhone.trim(),
      });

      setNewContactType("Client");
      setNewContactCustomType("");
      setNewName("");
      setNewOrg("");
      setNewPhone("");
      setNewEmail("");
      toast.success("Contact saved");
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save contact"));
    }
    setSavingContact(false);
  }, [newName, newOrg, newPhone, newEmail, newContactType, newContactCustomType, onSelect]);

  // A name filled in from outside (e.g. the matter page's Invoice request
  // button) shows as chosen, not as an empty search box.
  useEffect(() => {
    if (selectedName && !query && !selected) setSelected(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName]);

  const isSelected = selected && selectedName;

  return (
    <div className={className}>
      <Label className="text-sm font-medium">
        {label} <span className="text-status-red">*</span>
      </Label>

      {isSelected ? (
        <div className="flex items-center gap-2 px-3 py-2 mt-1 rounded-md border border-teal/40 bg-teal/5">
          <User className="w-4 h-4 text-teal shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate block">{selectedName}</span>
            {selectedEmail && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Mail className="w-3 h-3" />{selectedEmail}
              </span>
            )}
          </div>
          <button onClick={handleClear} className="text-gray-400 hover:text-gray-600 p-0.5" type="button">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={handleQueryChange}
            placeholder="Search contacts..."
            className="pl-9"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}

          {results.length > 0 && (
            <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-gray-200 rounded-md bg-white max-h-56 overflow-y-auto shadow-lg">
              {results.map((c, i) => (
                <div
                  key={i}
                  onClick={() => selectContact(c)}
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <div className="text-sm font-medium text-gray-900 truncate">{c.display_name || c.name}</div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {c.primary_phone && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />{c.primary_phone}
                      </span>
                    )}
                    {(c.primary_email || c.email) && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />{c.primary_email || c.email}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {query.length >= 2 && !searching && results.length === 0 && !showNewForm && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => {
                setShowNewForm(true);
                setNewName(query);
                setResults([]);
              }}
              className="mt-2 text-xs text-teal hover:text-teal hover:bg-teal/5 w-full justify-start gap-1.5"
            >
              <UserPlus className="w-3.5 h-3.5" />Save as contact
            </Button>
          )}
        </div>
      )}

      {/* Linked matters dropdown */}
      {isSelected && (loadingMatters || linkedMatters.length > 0) && (
        <div className="mt-3">
          <Label className="text-sm font-medium">Linked matters</Label>
          {loadingMatters ? (
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading matters...
            </div>
          ) : (
            <div className="mt-1 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
              {linkedMatters.map((m, i) => {
                const ref = m.reference || m.matter_reference || m.matter_ref || "";
                const title = m.matter_title || m.title || m.client_name || "";
                return (
                  <div
                    key={i}
                    onClick={() => selectMatter(m)}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{ref}</div>
                    {title && <div className="text-xs text-gray-500">{title}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Inline new-contact form */}
      {showNewForm && (
        <div className="border border-gray-200 rounded-md p-4 space-y-3 mt-3 bg-gray-50">
          <div className="text-sm font-medium text-teal flex items-center gap-1.5">
            <UserPlus className="w-4 h-4" />New Contact
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Contact type *</Label>
            <RadioGroup value={newContactType} onValueChange={setNewContactType} className="flex flex-wrap gap-1.5">
              {CONTACT_TYPES.map(t => (
                <label key={t} className={`flex items-center gap-1 px-2 py-1 rounded text-xs border cursor-pointer transition-colors ${newContactType === t ? "bg-teal text-white border-teal" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                  <RadioGroupItem value={t} className="sr-only" />
                  {t}
                </label>
              ))}
            </RadioGroup>
            {newContactType === "Other" && (
              <Input value={newContactCustomType} onChange={e => setNewContactCustomType(e.target.value)}
                placeholder="Specify type..." className="mt-1.5 text-sm h-8" />
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Name *</Label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name" className="text-sm h-8" />
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Organisation</Label>
            <Input value={newOrg} onChange={e => setNewOrg(e.target.value)} placeholder="Optional" className="text-sm h-8" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Phone</Label>
              <Input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="01234 567890" className="text-sm h-8" />
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Email</Label>
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Optional" className="text-sm h-8" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" type="button" onClick={saveNewContact} disabled={savingContact}
              className="flex-1 bg-teal hover:bg-teal-hover text-white text-xs h-8">
              {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save Contact"}
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setShowNewForm(false)}
              className="text-xs text-gray-500 hover:text-gray-700 h-8">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
